import {
  Container,
  Paper,
  Button,
  Center,
  Text,
  Select as MantineSelect,
} from "@mantine/core";
import { useForm } from "react-hook-form";
import {
  TextInput,
  Textarea,
  SegmentedControl,
  Select,
  MultiSelect,
  DateTimePicker,
} from "react-hook-form-mantine";
import "@mantine/dates/styles.css";
import { Ionicon } from "../Ionicon";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import {
  useCreateAnnouncement,
  useGetAllAnnouncements,
} from "../../api/announcements";
import { modals } from "@mantine/modals";
import moreThanOneAnnouncementModal from "./MoreThanOneAnnouncementModal";
import { useGetTags } from "../../api/tag";
import { useGetEvent } from "../../api/event";
import LocalizedInput from "../common/LocalizedInput";
import { web_createAnnouncementSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { makeI18nDefaultValue } from "../../utils/i18nUtils";
import AnnouncementLinkFields from "./AnnouncementLinkFields";
import dayjs from "dayjs";

const CreateAnnouncement = () => {
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const event = useGetEvent(currentEventId);

  const mainLanguage = event?.data?.language || "en";
  const otherLanguages = event?.data?.secondaryLanguages || [];

  const getDefaultValues = () => ({
    titleI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
    textI18n: makeI18nDefaultValue(mainLanguage, otherLanguages),
    type: "info",
    isVisible: "false",
    eventId: currentEventId,
    targetedTagIds: [],
    scheduledAt: null,
    linkType: "none",
    linkTargetId: null,
  });

  const { control, handleSubmit, setValue, reset, watch } = useForm({
    defaultValues: getDefaultValues(),
    resolver: resolver(web_createAnnouncementSchema),
  });

  const [schedule, setSchedule] = useState("now");
  const [pushNotification, setPushNotification] = useState("manual");

  const { data } = useGetAllAnnouncements({
    eventId: currentEventId,
    getAll: true,
    page: 1,
    limit: 20,
  });

  const { data: tags } = useGetTags({
    eventId: currentEventId,
    type: "people",
  });

  const tagsOptions = (tags ?? []).map((tag) => ({
    value: String(tag.id),
    label: `${tag.title} - (${tag.usageCount} users)`,
  }));

  const moreThanOneVisible =
    data?.data.filter((announcement) => announcement.isVisible).length >= 1;

  const { mutate, isPending } = useCreateAnnouncement();

  // Set eventId value to currentEventId If user changes current event, we need
  // to update eventId value in the form.
  useEffect(() => {
    setValue("eventId", currentEventId);
  }, [currentEventId, setValue]);

  // When scheduling for later, force visibility to hidden
  useEffect(() => {
    if (schedule === "later") {
      setValue("isVisible", "false");
    }
  }, [schedule, setValue]);

  // Reset form with proper i18n defaults when event languages are loaded
  useEffect(() => {
    if (event?.data) {
      reset(getDefaultValues());
    }
  }, [event?.data?.language, event?.data?.secondaryLanguages]);

  const onSubmit = (data) => {
    if (data.isVisible) {
      data.isVisible = data.isVisible === "true";
    }

    // Handle scheduling
    if (schedule === "later") {
      data.scheduledAt = data.scheduledAt
        ? new Date(data.scheduledAt).toISOString()
        : null;
      data.isVisible = false;
      data.shouldAutoNotify = pushNotification === "automatic";
    } else {
      delete data.scheduledAt;
    }

    // No tags selected targets all ticket holders
    data.targetedTagIds = data.targetedTagIds?.length
      ? data.targetedTagIds.map(Number)
      : null;

    data.linkType = data.linkType === "none" ? null : data.linkType;
    data.linkTargetId = data.linkTargetId ? Number(data.linkTargetId) : null;

    if (data.isVisible && moreThanOneVisible) {
      const onlyThisCallback = () => {
        mutate(
          { ...data, isOnlyThisVisible: true },
          {
            onSuccess: () => {
              reset();
              setSchedule("now");
              setPushNotification("manual");
              modals.closeAll();
            },
          }
        );
      };

      const showBothCallback = () => {
        mutate(data, {
          onSuccess: () => {
            reset();
            setSchedule("now");
            setPushNotification("manual");
            modals.closeAll();
          },
        });
      };

      moreThanOneAnnouncementModal(showBothCallback, onlyThisCallback);
    } else {
      mutate(data, {
        onSuccess: () => {
          reset();
          setSchedule("now");
          setPushNotification("manual");
        },
      });
    }
  };

  const localizedInputFields = [
    {
      nameBase: "titleI18n",
      label: "Title",
      component: TextInput,
      props: {
        mt: "sm",
        withAsterisk: true,
        placeholder: "Your title",
      },
    },
    {
      nameBase: "textI18n",
      label: "Text",
      component: Textarea,
      props: {
        rows: 2,
        mt: "sm",
        placeholder: "Enter description text",
      },
    },
  ];

  return (
    <Container size={500}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <form onSubmit={handleSubmit((data) => onSubmit(data))}>
          <LocalizedInput
            control={control}
            fields={localizedInputFields}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />
          <Text size="sm" fw={500} mt="md" style={{ userSelect: "none" }}>
            Appearance
          </Text>
          <SegmentedControl
            name="type"
            control={control}
            data={[
              {
                value: "info",
                label: (
                  <Center style={{ gap: 10 }}>
                    <Ionicon name="information-circle-outline" />
                    <span>Info</span>
                  </Center>
                ),
              },
              {
                value: "warning",
                label: (
                  <Center style={{ gap: 10 }}>
                    <Ionicon name="warning-outline" />
                    <span>Warning</span>
                  </Center>
                ),
              },
            ]}
            fullWidth
          />
          <MultiSelect
            label="Targeted announcement"
            description="Leave empty to target all ticket holders"
            placeholder="All ticket holders"
            mt="sm"
            name="targetedTagIds"
            control={control}
            data={tagsOptions}
            searchable
            clearable
          />
          <AnnouncementLinkFields
            control={control}
            watch={watch}
            setValue={setValue}
            eventId={currentEventId}
          />
          <MantineSelect
            label="Schedule"
            mt="sm"
            value={schedule}
            onChange={(value) => setSchedule(value)}
            data={[
              { value: "now", label: "Right now" },
              { value: "later", label: "Schedule for later" },
            ]}
            allowDeselect={false}
          />
          {schedule === "later" && (
            <>
              <DateTimePicker
                control={control}
                name="scheduledAt"
                label="Scheduled date and time"
                placeholder="Select date and time"
                mt="sm"
                clearable
                minDate={dayjs().format("YYYY-MM-DD")}
                value={watch("scheduledAt") ?? null}
                onChange={(date) =>
                  setValue("scheduledAt", date ?? null, {
                    shouldDirty: true,
                  })
                }
              />
              <MantineSelect
                label="Push notification"
                mt="sm"
                value={pushNotification}
                onChange={(value) => setPushNotification(value)}
                data={[
                  { value: "automatic", label: "Automatic" },
                  { value: "manual", label: "Manual" },
                ]}
                allowDeselect={false}
              />
              <Text size="xs" c="dimmed" mt={4}>
                {pushNotification === "automatic"
                  ? "Push notification for this Announcement will be sent out at the scheduled time."
                  : "This Announcement will appear on the homepage of users at scheduled time without the push notification."}
              </Text>
            </>
          )}
          <Select
            label="Visibility"
            placeholder="Select visibility"
            mt="sm"
            name="isVisible"
            control={control}
            data={[
              { value: "true", label: "Visible" },
              { value: "false", label: "Hidden" },
            ]}
            allowDeselect={false}
            disabled={schedule === "later"}
          />
          <Button type="submit" mt="sm" loading={isPending}>
            Create announcement
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateAnnouncement;
